import { supabase } from './supabase';
import { analyticsApi, leadsApi } from './api';

interface GeoLocationResult {
  city: string;
  country: string;
  ip: string;
  source: 'cache' | 'ip-api' | 'browser' | 'fallback' | 'user';
}

const DEFAULT_CITY = 'Chennai';

const KNOWN_CITY_COORDS: Record<string, [number, number]> = {
  'Chennai': [13.0827, 80.2707],
  'Coimbatore': [11.0168, 76.9558],
  'Madurai': [9.9252, 78.1198],
  'Trichy': [10.7905, 78.7047],
  'Salem': [11.6643, 78.1460],
  'Tirunelveli': [8.7139, 77.7567],
  'Vellore': [12.9165, 79.1325],
  'Erode': [11.3410, 77.7172],
  'Bengaluru': [12.9716, 77.5946],
  'Hyderabad': [17.3850, 78.4867],
  'Mumbai': [19.0760, 72.8777],
  'Pune': [18.5204, 73.8567],
  'Delhi NCR': [28.6139, 77.2090],
  'Kolkata': [22.5726, 88.3639],
  'Ahmedabad': [23.0225, 72.5714],
  'Jaipur': [26.9124, 75.7873],
  'Kochi': [9.9312, 76.2673],
  'Noida': [28.5355, 77.3910],
  'Gurgaon': [28.4595, 77.0266],
  'Chandigarh': [30.7333, 76.7794],
  'Lucknow': [26.8467, 80.9462],
  'Indore': [22.7196, 75.8577],
  'Bhopal': [23.2599, 77.4126],
  'Visakhapatnam': [17.6868, 83.2185]
};

function getNearestIndianCity(lat: number, lon: number): string {
  let nearestCity = DEFAULT_CITY;
  let minDistance = Infinity;

  for (const [cityName, coords] of Object.entries(KNOWN_CITY_COORDS)) {
    const dLat = (lat - coords[0]) * Math.PI / 180;
    const dLon = (lon - coords[1]) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(coords[0] * Math.PI / 180) * Math.cos(lat * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const dist = 6371 * c; // in km

    if (dist < minDistance) {
      minDistance = dist;
      nearestCity = cityName;
    }
  }

  return nearestCity;
}

export async function requestBrowserLocation(): Promise<{ city: string; lat: number; lng: number } | null> {
  if (typeof window === 'undefined' || !navigator?.geolocation) {
    return null;
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        let detected = getNearestIndianCity(lat, lon);

        // Reverse-geocode via Nominatim for higher precision if available
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 2000);
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`, {
            headers: { 'Accept-Language': 'en' },
            signal: controller.signal
          });
          clearTimeout(timeoutId);
          if (res.ok) {
            const data = await res.json();
            const addrCity = data.address?.city || data.address?.town || data.address?.state_district || data.address?.county;
            if (addrCity) {
              // Match to known city or use cleaned name
              const matched = Object.keys(KNOWN_CITY_COORDS).find(k => k.toLowerCase() === addrCity.toLowerCase());
              if (matched) detected = matched;
            }
          }
        } catch {}

        try {
          localStorage.setItem('localhub_user_geo', JSON.stringify({
            city: detected,
            country: 'India',
            ip: '127.0.0.1',
            source: 'browser',
            timestamp: Date.now()
          }));
        } catch {}

        resolve({ city: detected, lat, lng: lon });
      },
      (err) => {
        console.warn('Browser geolocation permission dismissed or denied:', err.message);
        resolve(null);
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  });
}

// User-approved or default city detection (Default: Chennai, never inaccurate third-party ISP hubs)
export async function detectVisitorCity(): Promise<GeoLocationResult> {
  // 1. Check local storage cache
  try {
    const cached = localStorage.getItem('localhub_user_geo');
    if (cached) {
      const parsed = JSON.parse(cached);
      // Discard inaccurate third-party ip-api cache
      if (parsed && parsed.source !== 'ip-api' && parsed.city && parsed.timestamp && Date.now() - parsed.timestamp < 30 * 24 * 60 * 60 * 1000) {
        return {
          city: parsed.city,
          country: parsed.country || 'India',
          ip: parsed.ip || '127.0.0.1',
          source: parsed.source || 'cache',
        };
      }
    }
  } catch (e) {}

  // 2. Default is Chennai (ZhaSearch native market)
  const fallback: GeoLocationResult = {
    city: DEFAULT_CITY,
    country: 'India',
    ip: '127.0.0.1',
    source: 'fallback',
  };

  try {
    localStorage.setItem('localhub_user_geo', JSON.stringify({ ...fallback, timestamp: Date.now() }));
  } catch (e) {}

  return fallback;
}

export function setUserCity(city: string) {
  try {
    const cached = localStorage.getItem('localhub_user_geo');
    const existing = cached ? JSON.parse(cached) : {};
    localStorage.setItem('localhub_user_geo', JSON.stringify({ ...existing, city, timestamp: Date.now() }));
  } catch {}
}

export async function trackLandingPageVisit(pagePath: string, detectedCity: string, ipAddress?: string) {
  try {
    const page = pagePath || '/';
    const city = detectedCity || DEFAULT_CITY;
    const today = new Date().toISOString().split('T')[0];

    void analyticsApi.recordEvent({
      event_type: 'page_view',
      city: city,
      category: page,
      meta: {
        ip: ipAddress || '127.0.0.1',
        landing_page: page,
        date: today,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      }
    });
  } catch (err) {}
}

export interface BusinessEnquiryItem {
  id: string;
  name: string;
  phone: string;
  message: string;
  listing_name: string;
  listing_id: string;
  category: string;
  city: string;
  ip: string;
  created_at: string;
}

export interface JourneyLogItem {
  id: string;
  ip: string;
  user_origin_city: string;
  target_city: string;
  user_name?: string;
  user_phone?: string;
  page_url: string;
  action: string;
  listing_name?: string;
  category?: string;
  created_at: string;
}

export async function recordBusinessEnquiry(enquiry: Omit<BusinessEnquiryItem, 'id' | 'created_at'>): Promise<BusinessEnquiryItem> {
  const item: BusinessEnquiryItem = {
    ...enquiry,
    id: `enq-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    created_at: new Date().toISOString()
  };

  try {
    const existing = localStorage.getItem('localhub_business_enquiries');
    const list: BusinessEnquiryItem[] = existing ? JSON.parse(existing) : [];
    localStorage.setItem('localhub_business_enquiries', JSON.stringify([item, ...list].slice(0, 200)));
  } catch {}

  saveIpUserMapping(item.ip, item.name, item.phone);

  try {
    void leadsApi.createLead({
      customer_name: item.name,
      customer_phone: item.phone,
      listing_id: item.listing_id || undefined,
      service_needed: item.message,
      city: item.city
    });
    void analyticsApi.recordEvent({
      event_type: 'enquiry',
      listing_id: item.listing_id || undefined,
      city: item.city,
      category: item.category,
      meta: {
        customer_name: item.name,
        customer_phone: item.phone,
        message: item.message,
        ip: item.ip
      }
    });
  } catch {}

  recordVisitorJourney({
    ip: item.ip,
    user_origin_city: 'Chennai',
    target_city: item.city,
    user_name: item.name,
    user_phone: item.phone,
    page_url: `/b/${item.listing_id}`,
    action: `Submitted Enquiry: "${item.message.slice(0, 40)}..."`,
    listing_name: item.listing_name,
    category: item.category
  });

  return item;
}

export function getBusinessEnquiries(): BusinessEnquiryItem[] {
  try {
    const saved = localStorage.getItem('localhub_business_enquiries');
    if (saved) return JSON.parse(saved);
  } catch {}
  return [];
}

export function recordVisitorJourney(entry: {
  ip: string;
  user_origin_city?: string;
  target_city?: string;
  user_name?: string;
  user_phone?: string;
  page_url: string;
  action: string;
  listing_name?: string;
  category?: string;
}) {
  let knownName = entry.user_name;
  let knownPhone = entry.user_phone;
  if (!knownName) {
    const enquiries = getBusinessEnquiries();
    const match = enquiries.find(e => e.ip === entry.ip);
    if (match) {
      knownName = match.name;
      knownPhone = match.phone;
    }
  }

  const logItem: JourneyLogItem = {
    id: `j-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    ip: entry.ip || '103.115.96.14',
    user_origin_city: entry.user_origin_city || DEFAULT_CITY,
    target_city: entry.target_city || entry.user_origin_city || DEFAULT_CITY,
    user_name: knownName,
    user_phone: knownPhone,
    page_url: entry.page_url,
    action: entry.action,
    listing_name: entry.listing_name,
    category: entry.category,
    created_at: new Date().toISOString()
  };

  try {
    const existing = localStorage.getItem('localhub_visitor_journey_logs');
    const list: JourneyLogItem[] = existing ? JSON.parse(existing) : [];
    localStorage.setItem('localhub_visitor_journey_logs', JSON.stringify([logItem, ...list].slice(0, 500)));
  } catch {}
}

export function getVisitorJourneyLogs(): JourneyLogItem[] {
  try {
    const saved = localStorage.getItem('localhub_visitor_journey_logs');
    if (saved) return JSON.parse(saved);
  } catch {}
  return [];
}

export function saveIpUserMapping(ip: string, name: string, phone: string) {
  try {
    const existingStr = localStorage.getItem('localhub_ip_user_map');
    const map = existingStr ? JSON.parse(existingStr) : {};
    map[ip] = { name, phone, hasSubmittedLead: true };
    localStorage.setItem('localhub_ip_user_map', JSON.stringify(map));
  } catch {}
}

export function getIpUserMap(): Record<string, { name: string; phone: string; hasSubmittedLead: boolean }> {
  const map: Record<string, { name: string; phone: string; hasSubmittedLead: boolean }> = {};
  try {
    const saved = localStorage.getItem('localhub_ip_user_map');
    if (saved) Object.assign(map, JSON.parse(saved));
  } catch {}
  return map;
}

// 🛍️ E-Commerce Leads Helpers
export interface EcommerceLeadItem {
  id: string;
  order_id?: string;
  name?: string;
  buyer_name: string;
  phone?: string;
  buyer_phone: string;
  buyer_email?: string;
  email?: string;
  product_name: string;
  product_id?: string;
  merchant_name?: string;
  city?: string;
  price?: number;
  quantity?: number;
  delivery_address?: string;
  enquiry_type?: string;
  status: 'new' | 'contacted' | 'processing' | 'shipped' | 'completed' | 'cancelled' | string;
  notes?: string;
  created_at: string;
}

export function getEcommerceLeads(): EcommerceLeadItem[] {
  try {
    const saved = localStorage.getItem('localhub_ecommerce_leads');
    if (saved) return JSON.parse(saved);
  } catch {}
  return [];
}

export function updateEcommerceLeadStatus(id: string, status: EcommerceLeadItem['status'], notes?: string): EcommerceLeadItem[] {
  try {
    const list = getEcommerceLeads();
    const updated = list.map(item => item.id === id ? { ...item, status, notes: notes ?? item.notes } : item);
    localStorage.setItem('localhub_ecommerce_leads', JSON.stringify(updated));
    return updated;
  } catch {
    return [];
  }
}

// 🏢 Real Estate Leads Helpers
export interface RealEstateLeadItem {
  id: string;
  project_id?: string;
  project_slug?: string;
  project_title?: string;
  project_name: string;
  developer_name?: string;
  project_city?: string;
  city?: string;
  locality?: string;
  lead_type?: 'site_visit' | 'brochure' | 'enquiry' | 'pricing' | string;
  name: string;
  phone: string;
  ip?: string;
  email?: string;
  preferred_date?: string;
  preferred_slot?: string;
  source?: string;
  message?: string;
  status: 'new' | 'contacted' | 'scheduled' | 'visited' | 'visit_scheduled' | 'visit_completed' | 'booked' | 'closed' | 'dropped' | string;
  notes?: string;
  created_at: string;
}

export function getRealEstateLeads(): RealEstateLeadItem[] {
  try {
    const saved = localStorage.getItem('localhub_realestate_leads');
    if (saved) return JSON.parse(saved);
  } catch {}
  return [];
}

export function updateRealEstateLeadStatus(id: string, status: RealEstateLeadItem['status'], notes?: string): RealEstateLeadItem[] {
  try {
    const list = getRealEstateLeads();
    const updated = list.map(item => item.id === id ? { ...item, status, notes: notes ?? item.notes } : item);
    localStorage.setItem('localhub_realestate_leads', JSON.stringify(updated));
    return updated;
  } catch {
    return [];
  }
}

export async function recordRealEstateLead(lead: Partial<RealEstateLeadItem>): Promise<RealEstateLeadItem> {
  const item: RealEstateLeadItem = {
    id: `re-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    created_at: new Date().toISOString(),
    status: 'new',
    name: lead.name || 'Anonymous Visitor',
    phone: lead.phone || '',
    project_name: lead.project_name || lead.project_title || 'Featured Project',
    ...lead
  };
  try {
    const list = getRealEstateLeads();
    localStorage.setItem('localhub_realestate_leads', JSON.stringify([item, ...list].slice(0, 300)));
  } catch {}
  return item;
}

// 🖼️ Image Library Admin Helpers
export interface CustomLibraryImage {
  id: string;
  url: string;
  title: string;
  category: 'gallery' | 'floor_plan' | 'floorplan' | 'locality' | 'product' | 'ecommerce' | string;
  city?: string;
  tags?: string[];
  uploaded_at: string;
}

export function getCustomLibraryImages(): CustomLibraryImage[] {
  try {
    const saved = localStorage.getItem('localhub_custom_image_library');
    if (saved) return JSON.parse(saved);
  } catch {}
  return [];
}

export function saveCustomLibraryImage(img: Partial<CustomLibraryImage>): CustomLibraryImage {
  const fullImg: CustomLibraryImage = {
    id: img.id || `img-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    url: img.url || '',
    title: img.title || 'Untitled Image',
    category: img.category || 'gallery',
    city: img.city || 'Chennai',
    tags: img.tags || [],
    uploaded_at: img.uploaded_at || new Date().toISOString()
  };
  try {
    const list = getCustomLibraryImages();
    localStorage.setItem('localhub_custom_image_library', JSON.stringify([fullImg, ...list]));
  } catch {}
  return fullImg;
}

export function deleteCustomLibraryImage(id: string): CustomLibraryImage[] {
  try {
    const list = getCustomLibraryImages();
    const updated = list.filter(i => i.id !== id);
    localStorage.setItem('localhub_custom_image_library', JSON.stringify(updated));
    return updated;
  } catch {
    return [];
  }
}
